import { useState, useEffect } from "react";
import { Leaf, Crown, Check, X, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const BASIC = ["Create & customize your page", "Upload posts", "Use AI or Not", "Community feed", "Basic support"];
const PRO = ["Everything in Basic", "Send invoices", "Direct messaging earning", "New features first", "Priority support"];

export default function PlanGate() {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    const h = (e) => { setInfo(e.detail); setOpen(true); };
    window.addEventListener("plan-gate", h);
    return () => window.removeEventListener("plan-gate", h);
  }, []);

  const choose = async (tier) => {
    setBusy(tier);
    try {
      const { data } = await api.post("/membership/checkout", { tier, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Checkout failed"); setBusy(""); }
  };

  if (!open) return null;
  const proOnly = info?.code === "pro_required";
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="plan-gate-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <div className="relative w-full max-w-md bg-ink-900 border border-zinc-800 rounded-2xl p-6">
        <button onClick={() => setOpen(false)} data-testid="plan-gate-close" className="absolute top-4 right-4 text-zinc-500 hover:text-white"><X size={20} /></button>
        <h2 className="font-heading font-black text-2xl mb-1">{proOnly ? "Upgrade to Pro" : "Choose a plan to continue"}</h2>
        <p className="text-zinc-400 text-sm mb-5">{info?.message || "This feature needs an active plan."} You can still scroll the feed and use your AI credits anytime.</p>
        <div className="space-y-3">
          {!proOnly && (
            <div className="rounded-xl border border-zinc-800 bg-ink-950 p-4" data-testid="gate-basic">
              <div className="flex items-center gap-3 mb-2">
                <Leaf className="text-green-500" size={22} />
                <div className="flex-1"><div className="font-heading font-bold">Basic</div><div className="text-xs text-zinc-500">Core features</div></div>
                <div className="text-right"><span className="font-heading font-black text-xl text-gold">$0.99</span><div className="text-[11px] text-zinc-500">per year</div></div>
              </div>
              <ul className="grid grid-cols-1 gap-1 mb-3">{BASIC.slice(0, 3).map((f) => <li key={f} className="flex items-center gap-2 text-xs text-zinc-300"><Check size={13} className="text-blue-400" /> {f}</li>)}</ul>
              <button onClick={() => choose("basic")} disabled={busy} data-testid="gate-choose-basic" className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white font-heading font-bold py-2.5 rounded-lg hover:bg-blue-500 disabled:opacity-50">{busy === "basic" ? <Loader2 className="animate-spin" size={16} /> : "Choose Basic"}</button>
            </div>
          )}
          <div className="relative rounded-xl border border-green-500 bg-ink-950 p-4 shadow-[0_0_24px_-6px_rgba(34,197,94,0.5)]" data-testid="gate-pro">
            <div className="absolute -top-2.5 right-4 bg-green-500 text-ink-950 text-[10px] font-heading font-bold px-2 py-0.5 rounded-full uppercase">Most Popular</div>
            <div className="flex items-center gap-3 mb-2">
              <Crown className="text-green-500" size={22} />
              <div className="flex-1"><div className="font-heading font-bold">Pro</div><div className="text-xs text-zinc-500">Grow and earn</div></div>
              <div className="text-right"><span className="font-heading font-black text-xl text-gold">$0.99</span><div className="text-[11px] text-zinc-500">per month</div></div>
            </div>
            <ul className="grid grid-cols-1 gap-1 mb-3">{PRO.slice(0, 3).map((f) => <li key={f} className="flex items-center gap-2 text-xs text-zinc-300"><Check size={13} className="text-blue-400" /> {f}</li>)}</ul>
            <button onClick={() => choose("pro")} disabled={busy} data-testid="gate-choose-pro" className="w-full flex items-center justify-center gap-2 bg-green-500 text-ink-950 font-heading font-bold py-2.5 rounded-lg hover:bg-green-400 disabled:opacity-50">{busy === "pro" ? <Loader2 className="animate-spin" size={16} /> : "Choose Pro"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
